/* eslint-disable no-console */
// FIRST, before anything that can reach the router — see adminLoginHandoff.js.
import "@/common/adminLoginHandoff";
import { createApp } from "vue";
import { createHead } from "@unhead/vue";
import VeeValidateCompat from "@/plugins/veeValidateCompat";
import EventBusPlugin from "@/plugins/eventBus";
import VueToast from "vue-toast-notification";
import VueFormWizard from "vue-form-wizard";
import "vue-toast-notification/dist/theme-sugar.css";
import "vuejs-dialog/dist/vuejs-dialog.min.css";
//select/dropdown option vue component
import "vue-select/dist/vue-select.css";
import vSelect from "vue-select";
import moment from "moment";
import { CkeditorPlugin as CKEditor } from "@ckeditor/ckeditor5-vue";
import "croppie/croppie.css";
import VueCroppieCompat from "@/components/VueCroppieCompat.vue";
import { createBootstrap, BPagination } from "bootstrap-vue-next";
import "@/mixins/commonMixin";
import commonMixin from "@/mixins/commonMixin";

// Vue Tags
import VueTagsInput from "@johmun/vue-tags-input";

import App from "./App.vue";
import router from "./router";
import store from "./store";
//import "./registerServiceWorker";

import {
    CHECK_AUTH,
    FETCH_ROLLUP_FEES,
    FETCH_ECOMM_DISPLAY_NAMES
} from "./store/actions.type";
import ApiService from "./common/api.service";
import DateFilter from "./common/date.filter";
import ErrorFilter from "./common/error.filter";
//####Import front-end header footer component//
import RwvHeader from "@/components/frontend/TheHeader";
import RwvFooter from "@/components/frontend/TheFooter";
import ProductZoomer from "./components/vue-product-zoomer/src//components/ProductZoomer.vue";

import JwtService from "@/common/jwt.service";
import { reloadForPreviewIfNeeded } from "@/common/previewBootstrap";

const app = createApp(App);

const head = createHead();
app.use(head);

app.use(store);
app.use(EventBusPlugin);
app.use(router);

app.use(createBootstrap());
app.use(VueToast, { duration: 2500 });
app.use(VueFormWizard);
app.use(CKEditor);
app.component("vue-croppie", VueCroppieCompat);

// vee-validate v2 compat shim: registers v-validate directive + errors mixin
app.use(VeeValidateCompat);

// floating-vue (v-tooltip replacement): directives registered globally
import { vTooltip, vClosePopper, Dropdown } from "floating-vue";
import "floating-vue/dist/style.css";
app.directive("tooltip", vTooltip);
app.directive("close-popover", vClosePopper);
app.component("VPopover", Dropdown);

app.mixin(commonMixin);

// Vue 2 reactivity shims — Vue 3 Proxy tracks direct assignment natively
app.config.globalProperties.$set = (obj, key, value) => {
    obj[key] = value;
};
app.config.globalProperties.$delete = (obj, key) => {
    delete obj[key];
};

// Filters: permanent shim — templates use {{ $filters.formatDate(val) }}
app.config.globalProperties.$filters = {
    date: DateFilter,
    error: ErrorFilter,
    capitalize(value) {
        if (!value) return "";
        value = value.toString();
        return value.charAt(0).toUpperCase() + value.slice(1);
    },
    converToCamel(value) {
        if (!value) return "";
        return value.replace(
            /(^\w|\s\w)(\S*)/g,
            (_, m1, m2) => m1.toUpperCase() + m2.toLowerCase()
        );
    },
    formatDate(value) {
        if (value) {
            // Business operates in Mountain Time (MST/MDT) - always render dates
            // in that zone regardless of the viewer's browser/OS timezone.
            return new Date(value).toLocaleDateString("en-US", {
                timeZone: "America/Denver",
                month: "short",
                day: "2-digit",
                year: "numeric"
            });
        }
    },
    formatDateNow(value) {
        if (value) {
            return moment(String(value)).fromNow();
        }
    }
};

app.component("v-select", vSelect);
app.component("VueTagsInput", VueTagsInput);
app.component("RwvHeader", RwvHeader);
app.component("RwvFooter", RwvFooter);
app.component("ProductZoomer", ProductZoomer);

// Bootstrap-Vue-Next: explicit fallback for b-pagination kebab-case resolution
app.component("BPagination", BPagination);

app.config.productionTip = false;

// Debug: log all Vue render/lifecycle errors with component name and trace
app.config.warnHandler = (msg, instance, trace) => {
    console.warn(
        "[Vue warn]",
        msg,
        "\nComponent:",
        instance?.$options?.name || instance?.$options?.__name,
        "\nTrace:",
        trace
    );
};

if (!reloadForPreviewIfNeeded()) {
    ApiService.init();

    router.beforeEach((to, from, next) => {
        // If Authentication Required
        if (
            to.meta.authRequired &&
            !JwtService.getToken() &&
            to.name !== "home"
        ) {
            app.config.globalProperties.$toast.error(
                "Error: Please login to access this page",
                {
                    position: "top-right"
                }
            );
            next({ name: "home" });
            return;
        }
        // Update Page title
        document.title = to.meta.title || to.meta.pageTitle || "ImprintCity";

        if (JwtService.getToken()) {
            let getProfData = JwtService.getUserData();
            if (getProfData && getProfData != undefined) {
                if (getProfData.user.status === 0) {
                    app.config.globalProperties.$toast.error("Unauthorized: ", {
                        position: "top-right"
                    });
                    JwtService.destroyToken();
                    next("/");
                    return;
                }
            }
            if (getProfData) {
                let noAccessRedirected = false;
                let permission_data = JSON.parse(
                    getProfData.user.RolePermission.permission_id
                );
                permission_data[5].submodule_json.forEach((element) => {
                    if (to.meta.is_permission == element.name) {
                        if (!element.permissions.full) {
                            next({ path: "/no-access" });
                            noAccessRedirected = true;
                        }
                    }
                });
                if (!noAccessRedirected) {
                    Promise.all([store.dispatch(CHECK_AUTH)])
                        .then(() => {
                            if (
                                to.name == "ResetPassword" &&
                                store.state.auth.isAuthenticatedClient
                            ) {
                                next("/");
                                return;
                            }
                            next();
                        })
                        .catch(() => {
                            next();
                        });
                }
            } else {
                ApiService.setHeader();
                ApiService.get("user-profile")
                    .then(({ data }) => {
                        // Save UserData in Session
                        JwtService.saveUserData(data);
                        let noAccessRedirected = false;
                        let permission_data = JSON.parse(
                            data.user.RolePermission.permission_id
                        );
                        permission_data[5].submodule_json.forEach((element) => {
                            if (to.meta.is_permission == element.name) {
                                if (!element.permissions.full) {
                                    next({ path: "/no-access" });
                                    noAccessRedirected = true;
                                }
                            }
                        });
                        if (!noAccessRedirected) {
                            Promise.all([store.dispatch(CHECK_AUTH)])
                                .then(() => {
                                    if (
                                        to.name == "ResetPassword" &&
                                        store.state.auth.isAuthenticatedClient
                                    ) {
                                        next("/");
                                        return;
                                    }
                                    next();
                                })
                                .catch(() => {
                                    next();
                                });
                        }
                    })
                    .catch(function (error) {
                        if (error.response) {
                            // response error
                        } else if (error.request) {
                            // request error
                        } else {
                            let errorMsg = error.message;
                            let status = errorMsg.search(/401/);
                            if (status > 0) {
                                app.config.globalProperties.$toast.error(
                                    "Unauthorized: " + errorMsg,
                                    {
                                        position: "top-right"
                                    }
                                );
                                JwtService.destroyToken();
                            } else {
                                app.config.globalProperties.$toast.error(
                                    "Error: " + errorMsg,
                                    {
                                        position: "top-right"
                                    }
                                );
                            }
                        }
                        next();
                    });
            }
        } else {
            next();
        }
    });

    // Initialize rollup fees on app load
    store.dispatch(`rollupFees/${FETCH_ROLLUP_FEES}`).catch(() => {});

    // Initialize ecomm display names on app load
    store
        .dispatch(`ecommDisplayNames/${FETCH_ECOMM_DISPLAY_NAMES}`)
        .catch(() => {});

    app.mount("#app");
}
